/**
 * Configurable props for `formly-required-documents-checklist`.
 *
 * The field was originally authored for the document-onboarding flow (#19108).
 * Every prop below is optional. Props with runtime defaults document them below,
 * and existing form-builder configurations can override those defaults.
 */
/**
 * Weergave van de checklist. `cards` is de bestaande kaartenlijst en blijft de default, zodat
 * iedere bestaande formulierconfiguratie ongewijzigd rendert. `rows` is de compacte regelweergave
 * uit het onboarding-ontwerp (Figma 3802:165013) — instelbaar per veld in de form-builder onder
 * "Weergave", net als `tagsDisplayMode` bij het tags-veld.
 */
export const DocumentsDisplayModeEnum = <const>{
  Cards: 'cards',
  Rows: 'rows'
};
export type DocumentsDisplayMode = (typeof DocumentsDisplayModeEnum)[keyof typeof DocumentsDisplayModeEnum];

export interface RequiredDocumentsChecklistProps {
  /**
   * @deprecated Use `instanceModuleCode`. Kept for backwards-compat with
   * existing database configs that already set this key.
   */
  moduleCode?: string;
  /**
   * Instance/upload-target module code — the status-source read by this checklist
   * (document-records; the raw file lives in the `documents` Uploads module and is
   * referenced from records via `bestand`).
   * Resolution order: `instanceModuleCode` → `moduleCode` → `'document-records'`.
   */
  instanceModuleCode?: string;
  /** Module code for the document-template source list. Default: `'document-templates'`. */
  templateModuleCode?: string;
  /**
   * Hoofdtype slug used only to scope instance records in the OData load filter
   * and the client-side `uploaded` computed. It is not written to new records.
   *
   * Explicit `null` disables hoofdtype scoping in both filters.
   *
   * Default: `null`.
   */
  instanceHoofdtypeSlug?: string | null;
  /** OData filter for loading template entries. Default: the onboarding `showInOnboarding` filter. */
  templateFilter?: string;
  /**
   * Back-ref field on the instance-target module pointing at the host entry. Default:
   * `'entry.entryId'`. When the host entry is itself the document anchor (no nested
   * `entry.company`/`entry.user` of its own), the back-ref key selects which kind of anchor the
   * host is: `'entry.company'`, `'entry.user'`, or `'entry.asset'` (bedrijfsmiddel-host).
   */
  moduleLinkedField?: string;
  /**
   * Kaarten of compacte regels. Default: `cards`. Zie {@link DocumentsDisplayMode}.
   */
  documentsDisplayMode?: DocumentsDisplayMode;
  /** Opt-out for the "all required documents uploaded" wizard validator. Default: `true`. */
  enforceUploadValidator?: boolean;
  /**
   * Accepted file extensions/mime-types for the card's dropzone. A template may override this per
   * document via its own `acceptedTypes`. Validation happens on drop as well as on pick — the
   * `accept` attribute only filters the native picker.
   */
  acceptedTypes?: string[];
  /**
   * Size ceiling in MB enforced on drop/pick, shared with `formly-field-upload-document`'s prop of
   * the same name (see `upload-validation.util.ts`). No dedicated form-builder toggle for this
   * field type yet — config-only. Default: `DEFAULT_MAX_FILE_SIZE_MB` (50).
   */
  maxFileSizeMb?: number;
}

/**
 * NOTE — target roles are not a prop. They are read from `formState.onboardingForm`
 * (`entry.settings.roles`), published by the onboarding page, because they belong to the
 * onboarding flow rather than to this field's configuration.
 */
